import { ReactElement } from 'react'
import { Flex } from 'theme-ui'
import getEnglishStrings from 'utils/getEnglishStrings'
import Hero from './directus/Hero'
import ComponentFAQ from './directus/FAQ'
import ComponentMarkdown from './directus/Markdown'
import ComponentCTA from './directus/CTA'
import Page from './Page'
import ContentTopImg from 'images/content-top-img.png'
import AuthorAndPublishedDate from './UI/AuthorAndPublishedDate'

const defaultPadding = ['4rem', '4.8rem', '6.9rem']
const contentMaxWidth = '89rem'

export interface SeoPageContext {
  page: Queries.KnowledgeBaseQuery['directus']['seo_pages'][number]
}

type ComponentData = NonNullable<Queries.DirectusData_blocks['components']>[number]

export default function SeoPage({
  pageContext: { page },
}: {
  pageContext: SeoPageContext
}): ReactElement {
  const strings = getEnglishStrings(page.strings)
  return (
    <Page
      title={`${strings?.title} | Windscribe`}
      footerColour="contentAndFooterBackground"
      metaDescription={strings?.meta_description}
      metaKeywords={strings?.meta_keywords}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          lineHeight: '2.7rem',
          width: '100%',
        }}
      >
        <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
          {/** TODO: find a better way to do typing here than casting */}
          {page?.hero && (
            <Hero
              data={page.hero as Queries.DirectusData_component_hero}
              sxx={{ maxWidth: contentMaxWidth, px: '1.6rem' }}
            >
              <AuthorAndPublishedDate
                author={page.user_created?.first_name + ' ' + page.user_created?.last_name}
                publishedAt={new Date(page.date_created)}
              />
            </Hero>
          )}
          <ContentImage />
          <Flex
            sx={{
              width: '100%',
              flexDirection: 'column',
              backgroundColor: 'contentAndFooterBackground',
              alignItems: 'center',
              gap: '4.8rem',
              py: defaultPadding,
            }}
          >
            {page.blocks?.map((block, index) => (
              <Flex
                key={index}
                sx={{
                  flexDirection: 'column',
                  maxWidth: contentMaxWidth,
                  px: ['1.6rem', '4.8rem', 0],
                  width: '100%',
                  gap: '5.6rem',
                  alignItems: 'center',
                }}
              >
                <Component key={index} data={block as ComponentData} />
              </Flex>
            ))}
          </Flex>
        </Flex>
      </Flex>
    </Page>
  )
}

function Component({ data }: { data: ComponentData }): ReactElement {
  switch (data?.collection) {
    case 'component_markdown':
      return <ComponentMarkdown data={data.item as Queries.DirectusData_component_markdown} />
    case 'component_faq':
      return <ComponentFAQ data={data.item as Queries.DirectusData_component_faq} />
    case 'component_cta':
      return <ComponentCTA data={data.item as Queries.DirectusData_component_cta} />
  }
  return <></>
}

export function ContentImage(): ReactElement {
  return (
    <img
      src={ContentTopImg}
      sx={{
        mt: defaultPadding,
        width: '100vw',
        maxWidth: '124.8rem',
        height: ['3.8rem', '5.4rem'],
        overflow: 'hidden',
        objectFit: 'cover',
      }}
      alt="Divider"
    />
  )
}
